import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCentroMapaPadrao } from '../lib/estabelecimento';
import { reverseGeocode, carregarGoogleMaps, TEM_GOOGLE_MAPS } from '../lib/geo';

export interface LocalSelecionado {
  lat: number;
  lng: number;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
}

interface Props {
  centro?: [number, number];
  zoom?: number;
  altura?: string;
  posicaoInicial?: [number, number] | null;
  /** Posição controlada externamente (ex.: ao geocodificar o endereço digitado). */
  posicao?: [number, number] | null;
  onSelecionar: (local: LocalSelecionado) => void;
}

const LEGENDA = 'Clique no mapa para marcar o local — os campos de endereço são preenchidos automaticamente.';

/** Mapa clicável (itens 2.69/2.70). Usa Google Maps quando há chave; senão Leaflet. */
export function MapaSeletor(props: Props) {
  return TEM_GOOGLE_MAPS ? <MapaSeletorGoogle {...props} /> : <MapaSeletorLeaflet {...props} />;
}

async function reverseParaLocal(lat: number, lng: number): Promise<LocalSelecionado> {
  const rev = await reverseGeocode(lat, lng);
  return rev
    ? { lat, lng, logradouro: rev.logradouro, numero: rev.numero, bairro: rev.bairro, cidade: rev.cidade, uf: rev.uf, cep: rev.cep }
    : { lat, lng };
}

// ---------------------------------------------------------------------------
// Google Maps
// ---------------------------------------------------------------------------
function MapaSeletorGoogle({ centro = getCentroMapaPadrao(), zoom = 13, altura = '320px', posicaoInicial, posicao, onSelecionar }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const mapsRef = useRef<any>(null);
  const onSelRef = useRef(onSelecionar);
  onSelRef.current = onSelecionar;
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    carregarGoogleMaps().then((maps) => {
      if (!vivo) return;
      if (!maps || !containerRef.current) { setFalhou(true); return; }
      mapsRef.current = maps;
      const inicial = posicao ?? posicaoInicial;
      const center = inicial ? { lat: inicial[0], lng: inicial[1] } : { lat: centro[0], lng: centro[1] };
      const map = new maps.Map(containerRef.current, {
        center, zoom: inicial ? 16 : zoom,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });
      mapRef.current = map;
      if (inicial) markerRef.current = new maps.Marker({ position: center, map });
      map.addListener('click', async (e: any) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        if (markerRef.current) markerRef.current.setPosition({ lat, lng });
        else markerRef.current = new maps.Marker({ position: { lat, lng }, map });
        onSelRef.current(await reverseParaLocal(lat, lng));
      });
    });
    return () => { vivo = false; mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps || !posicao) return;
    const pos = { lat: posicao[0], lng: posicao[1] };
    if (markerRef.current) markerRef.current.setPosition(pos);
    else markerRef.current = new maps.Marker({ position: pos, map });
    map.panTo(pos);
    if (map.getZoom() < 14) map.setZoom(16);
  }, [posicao]);

  if (falhou) return <MapaSeletorLeaflet centro={centro} zoom={zoom} altura={altura} posicaoInicial={posicaoInicial} posicao={posicao} onSelecionar={onSelecionar} />;

  return (
    <div>
      <div ref={containerRef} style={{ height: altura, width: '100%', borderRadius: '10px' }} />
      <p className="mt-1.5 text-xs text-slate-500">{LEGENDA}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaflet (fallback sem chave Google)
// ---------------------------------------------------------------------------
function MapaSeletorLeaflet({ centro = getCentroMapaPadrao(), zoom = 13, altura = '320px', posicaoInicial, posicao, onSelecionar }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onSelRef = useRef(onSelecionar);
  onSelRef.current = onSelecionar;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const inicial = posicao ?? posicaoInicial;
    const map = L.map(containerRef.current).setView(inicial ?? centro, inicial ? 16 : zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    if (inicial) markerRef.current = L.marker(inicial).addTo(map);

    map.on('click', async (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
      else markerRef.current = L.marker([lat, lng]).addTo(map);
      onSelRef.current(await reverseParaLocal(lat, lng));
    });

    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !posicao) return;
    if (markerRef.current) markerRef.current.setLatLng(posicao);
    else markerRef.current = L.marker(posicao).addTo(map);
    if (!map.getBounds().contains(posicao)) map.setView(posicao, 16);
  }, [posicao]);

  return (
    <div>
      <div ref={containerRef} style={{ height: altura, width: '100%', borderRadius: '10px' }} />
      <p className="mt-1.5 text-xs text-slate-500">{LEGENDA}</p>
    </div>
  );
}
